import type {
  NotificationMode,
  PendingNotificationTargets,
} from '../NotificationMode'

export type UserAsset = {
  id?: string
  title?: string
  url: string
}

export type BaseUserLike = {
  type: 'user' | 'user_group'
  uuid: string
  familyName?: string | null
  givenName?: string | null
  isActive?: boolean
  name: string
  asset?: UserAsset
}

export type User = BaseUserLike & {
  type: 'user'
  activeWorkspaceMember?: boolean
  legacyId: string
}

export type UserGroup = BaseUserLike & {
  type: 'user_group'
  description?: string
  members: Array<User>
  memberCount?: number
  archived: boolean
}

export type UserLike = User | UserGroup

/**
 * Order-insensitive fingerprint of an id list: two lists that hold the same
 * ids in a different order produce the same string.
 */
const sortedJoin = (ids: string[]): string => [...ids].sort().join(',')

/**
 * Tells whether two people-picker memberships hold the same people. "Same"
 * means the same set of ids, whatever their order and whatever mix of users
 * and groups they name: surfaces store the two kinds as one flat list, and an
 * id is unique across both. An absent list is never the same as a present one,
 * even an empty one: the cell holds no value at all.
 */
export const isSameUsersSelectIds = (
  a: string[],
  b: string[] | undefined
): boolean => b !== undefined && sortedJoin(a) === sortedJoin(b)

/** A people-picker membership as the wire carries it. */
type Membership = Readonly<{
  user_ids?: ReadonlyArray<string> | null
  group_ids?: ReadonlyArray<string> | null
}>

/** The users and user groups on a value that are waiting to be notified. */
type NotificationTargets = { user_ids: string[]; group_ids: string[] }

/**
 * Users and user groups of a people-picker shape as one list: a group counts
 * as one target, exactly as it does on the server. Callers hold these as wide
 * unions of every field shape, so the narrowing happens here, and any other
 * field type's value reads as an empty membership.
 */
const memberIds = (value: unknown): ReadonlyArray<string> => {
  const membership = value as Membership | null | undefined
  return membership && 'user_ids' in membership
    ? [...(membership.user_ids ?? []), ...(membership.group_ids ?? [])]
    : []
}

/**
 * Who a People Picker value still owes a notification to once a new membership
 * is saved, mirroring the server's rule so the badge is right before the
 * response lands. Only `manual` holds anyone back: it keeps whoever was
 * already waiting and adds whoever is new, while `automatic` sends as people
 * are added and `off` sends nothing.
 */
export const pendingNotificationTargetsAfterUpdate = ({
  previous,
  next,
  notificationMode,
}: {
  previous: { value: unknown; pendingTargets: unknown }
  next: Membership
  notificationMode: NotificationMode | null | undefined
}): NotificationTargets => {
  const previousMembers = new Set(memberIds(previous.value))
  const previousPending = new Set(memberIds(previous.pendingTargets))

  const stillPending = (personId: string) =>
    notificationMode === 'manual' &&
    (previousPending.has(personId) || !previousMembers.has(personId))

  return {
    user_ids: (next.user_ids ?? []).filter(stillPending),
    group_ids: (next.group_ids ?? []).filter(stillPending),
  }
}

/**
 * The same rule as `pendingNotificationTargetsAfterUpdate`, for a picker the
 * person is still editing: the selection on screen stands in for the
 * membership about to be saved.
 */
export const pendingNotificationIds = ({
  liveIds,
  mode,
  previouslyPending,
  storedIds,
}: Readonly<{
  /** The selection on screen now, unsaved additions and removals included. */
  liveIds: ReadonlyArray<string>
  /** Absent on a value the server has not created yet, which notifies nobody. */
  mode: NotificationMode | undefined
  /** What the server last said was waiting. Absent means nothing was. */
  previouslyPending: PendingNotificationTargets | undefined
  /** The saved membership the server holds. */
  storedIds: ReadonlyArray<string>
}>): ReadonlySet<string> => {
  const { user_ids, group_ids } = pendingNotificationTargetsAfterUpdate({
    previous: {
      value: { user_ids: storedIds },
      pendingTargets: {
        user_ids: previouslyPending?.userIds,
        group_ids: previouslyPending?.groupIds,
      },
    },
    next: { user_ids: liveIds },
    notificationMode: mode,
  })

  return new Set([...user_ids, ...group_ids])
}
